import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Mail, Phone } from "lucide-react";

export default function FloatingContact({ contact }) {
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 400);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const wa = contact?.whatsapp?.replace(/\D/g, "");
  const waLink = `https://wa.me/${wa}?text=${encodeURIComponent("Hello Lieber Sourcing — I'd like to discuss a sourcing project.")}`;

  return (
    <div className="fixed bottom-5 right-5 md:bottom-7 md:right-7 z-[70]">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="bg-white border border-gold-subtle rounded-xl p-2 mb-3 w-60 shadow-[0_20px_50px_-15px_rgba(26,37,48,0.35)]"
            data-testid="floating-contact-menu"
          >
            <a href={waLink} target="_blank" rel="noreferrer" data-testid="floating-whatsapp" className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-cream transition-colors">
              <span className="w-9 h-9 rounded-lg bg-[#25D366]/12 flex items-center justify-center"><MessageCircle size={18} className="text-[#25D366]" /></span>
              <div><div className="text-navy text-sm font-semibold">WhatsApp</div><div className="text-ink font-body text-[11px]">Chat now</div></div>
            </a>
            <a href={`mailto:${contact?.email}`} data-testid="floating-email" className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-cream transition-colors">
              <span className="w-9 h-9 rounded-lg bg-barn/10 flex items-center justify-center"><Mail size={17} className="text-barn" /></span>
              <div><div className="text-navy text-sm font-semibold">Email</div><div className="text-ink font-body text-[11px] truncate">{contact?.email}</div></div>
            </a>
            <a href={`tel:${contact?.phone}`} className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-cream transition-colors">
              <span className="w-9 h-9 rounded-lg bg-navy-soft/12 flex items-center justify-center"><Phone size={16} className="text-navy-soft" /></span>
              <div><div className="text-navy text-sm font-semibold">Call</div><div className="text-ink font-body text-[11px]">{contact?.phone}</div></div>
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {(show || open) && (
          <motion.button
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => setOpen((o) => !o)}
            data-testid="floating-contact-btn"
            className="ml-auto flex items-center gap-2.5 bg-barn text-ivory rounded-full pl-4 pr-5 py-3.5 shadow-[0_12px_30px_-8px_rgba(178,58,46,0.6)]"
          >
            {open ? <X size={20} /> : (
              <span className="relative flex items-center justify-center">
                <MessageCircle size={20} />
                <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#25D366] soft-pulse" />
              </span>
            )}
            <span className="font-body text-sm font-semibold">{open ? "Close" : "Contact Now"}</span>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
